import { z } from 'zod';
import { ProtectiveActionSchema, InZoneCommunitySchema } from './protective-action';
import { IncidentCommandSchema } from './incident-command';

/**
 * Incident + operational-period contracts (SITREP v3.1).
 *
 * Per operator-cop-doctrine.md §8.2 decision (b) and §9.2, these are the two
 * cheap shared primitives that upgrade the ICS-209 SITREP (stable incident
 * number + Initial→Update→Final chain) and unlock partial ICS-202 authoring
 * later. An `incident` is promoted from a fire / hotspot / CAP alert (or declared
 * manually); `operational_periods` sub-divide its timeline; SITREPs anchor to a
 * period and auto-derive their report version.
 */

/* ------------------------------------------------------------------ *
 * Enums
 * ------------------------------------------------------------------ */

export const IncidentHazardTypeSchema = z.enum([
  'wildfire',
  'flood',
  'ice_storm',
  'cap_warning',
  'multi_hazard',
  'other',
]);
export type IncidentHazardType = z.infer<typeof IncidentHazardTypeSchema>;

export const IncidentStatusSchema = z.enum(['open', 'closed']);
export type IncidentStatus = z.infer<typeof IncidentStatusSchema>;

/**
 * R1/#477 incident-as-aggregator — the wildfire lifecycle phase (migration 0117;
 * values renamed in 0118, incidents.lifecycle_state). Vocabulary doctrine-validated
 * against Ontario IMS Guidance 2.0 (docs/ontario-ims-doctrine-reference.md): detected →
 * confirmed → owned (Incident Command established, §4.1/§4.5) → response (the active
 * "Response" phase, App A #68) → deactivated (demobilization → deactivation, §8).
 * ORTHOGONAL to `status` (open/closed) and Response Level; AUTHORITATIVE (advance to
 * 'deactivated' closes the incident). The ORDER is load-bearing — the UX derives
 * Advance(next) / Revert(prev) by index, and the locked render test pins this 5-tuple.
 */
export const INCIDENT_LIFECYCLE_STATES = [
  'detected',
  'confirmed',
  'owned',
  'response',
  'deactivated',
] as const;
export const IncidentLifecycleStateSchema = z.enum(INCIDENT_LIFECYCLE_STATES);
export type IncidentLifecycleState = z.infer<typeof IncidentLifecycleStateSchema>;

/**
 * The legal lifecycle transitions — server-authoritative gate AND the source the UX
 * derives its Advance/Revert controls from (so they can never diverge). BIDIRECTIONAL:
 * each state may step forward one phase OR de-escalate one phase (confirmed→detected …).
 * The first entry is always the forward step (except the terminal 'deactivated', which
 * has no forward — its sole entry is the reignition target). A hop to any state NOT in
 * this set is rejected (HTTP 409 invalid_transition).
 *
 * Adjacency holds for detected↔confirmed↔owned↔response — EXCEPT the one reignition
 * back-edge: `deactivated → confirmed` (W7/#535) SKIPS 'response' and re-enters at the
 * last UN-COMMANDED phase. Deactivation (§8) stands command down (every live
 * incident_command row is released), so re-entering at 'confirmed' — not 'response' —
 * keeps `owned` ⟺ ≥1 live command (#489) airtight: a reignited incident is never
 * command-less in a commanded phase. The operator re-assumes Incident Command
 * (confirmed→owned via assume-command) to return to Response. This makes the graph
 * intentionally non-reciprocal at the deactivation/reignition pair.
 */
export const INCIDENT_LIFECYCLE_TRANSITIONS: Record<
  IncidentLifecycleState,
  readonly IncidentLifecycleState[]
> = {
  detected: ['confirmed'],
  confirmed: ['owned', 'detected'],
  owned: ['response', 'confirmed'],
  response: ['deactivated', 'owned'],
  deactivated: ['confirmed'], // W7/#535 — reignition re-enters at the last un-commanded phase (skips 'response')
};

/** The forward (advance) neighbour of a state, or null at the terminal 'deactivated'. */
export function nextLifecycleState(
  from: IncidentLifecycleState,
): IncidentLifecycleState | null {
  const i = INCIDENT_LIFECYCLE_STATES.indexOf(from);
  return i >= 0 && i < INCIDENT_LIFECYCLE_STATES.length - 1
    ? (INCIDENT_LIFECYCLE_STATES[i + 1] ?? null)
    : null;
}

/**
 * The de-escalation (revert / reignition) target of a state, or null at 'detected'.
 * Derived from INCIDENT_LIFECYCLE_TRANSITIONS as the legal transition that is NOT the
 * forward step — so it is consistent with the gate BY CONSTRUCTION (prevLifecycleState(s)
 * ∈ INCIDENT_LIFECYCLE_TRANSITIONS[s] always). For detected↔response this is the index-1
 * neighbour; for the terminal 'deactivated' it is the REIGNITION target 'confirmed'
 * (W7/#535 — re-enter at the last un-commanded phase, skipping 'response', so a reactivated
 * incident is never command-less). This is what the UX reads to build the Reactivate ←
 * control (#485 — operator verb; the backend edge is still the `reignited` token, §4.2), so the
 * affordance and the route can never diverge.
 */
export function prevLifecycleState(
  from: IncidentLifecycleState,
): IncidentLifecycleState | null {
  const forward = nextLifecycleState(from);
  return INCIDENT_LIFECYCLE_TRANSITIONS[from].find((t) => t !== forward) ?? null;
}

/** What an incident was promoted from (NULL for a manual incident). Fire literals
 *  FIRST (byte-stable for the fire path); flood/ice anomalies are the #425 un-gate
 *  (declare-from-any-hazard); operator_evac_order is the #462 evacuation-accounting
 *  bridge (an evacuation ORDER originates the incident its §4.30 accounting keys on,
 *  when no proximate open hazard incident exists to attach to). The DB column is free
 *  text + the read schema is z.string().nullable(), so widening this enum only affects
 *  the write boundary (+ the incidents.ts SOURCE_TABLE centroid map, in lockstep). */
export const IncidentSourceRecordTypeSchema = z.enum([
  'active_wildland_fire',
  'high_risk_hotspot',
  'cap_alert',
  'flood_anomaly',
  'ice_storm_anomaly',
  'operator_evac_order',
]);
export type IncidentSourceRecordType = z.infer<typeof IncidentSourceRecordTypeSchema>;

export const OperationalPeriodStatusSchema = z.enum(['planned', 'active', 'closed']);
export type OperationalPeriodStatus = z.infer<typeof OperationalPeriodStatusSchema>;

/* ------------------------------------------------------------------ *
 * Requests
 * ------------------------------------------------------------------ */

/**
 * POST /api/incidents — promote a source record (or declare manually) to a
 * tracked incident. When source_record_type + source_record_id are given the
 * promotion is idempotent (a second promote of the same record returns the
 * existing incident). geography is required (the incident centroid); when
 * promoting, the route may derive it from the source record if omitted.
 */
export const IncidentCreateSchema = z.object({
  name: z.string().min(1).max(256),
  hazard_type: IncidentHazardTypeSchema,
  /** Incident centroid; [lon, lat]. Optional when promoting (derived from source). */
  lon: z.number().min(-180).max(180).optional(),
  lat: z.number().min(-90).max(90).optional(),
  source_record_type: IncidentSourceRecordTypeSchema.optional(),
  source_record_id: z.string().uuid().optional(),
  metadata: z.record(z.unknown()).optional(),
  session_id: z.string().min(1).max(128),
});
export type IncidentCreate = z.infer<typeof IncidentCreateSchema>;

/** PATCH /api/incidents/:id — currently only close is supported. */
export const IncidentPatchSchema = z.object({
  status: z.literal('closed'),
  session_id: z.string().min(1).max(128),
});
export type IncidentPatch = z.infer<typeof IncidentPatchSchema>;

/**
 * POST /api/incidents/:id/advance — bidirectional lifecycle transition (R1/#477).
 * `to` must be an immediate neighbour of the incident's current lifecycle_state
 * (see INCIDENT_LIFECYCLE_TRANSITIONS) or the route returns 409. Audited under the
 * `advance_incident_lifecycle` verb with { from, to }.
 */
export const IncidentLifecycleAdvanceSchema = z.object({
  to: IncidentLifecycleStateSchema,
  session_id: z.string().min(1).max(128),
});
export type IncidentLifecycleAdvance = z.infer<typeof IncidentLifecycleAdvanceSchema>;

/** POST /api/incidents/:id/operational-periods — declare a new period. */
export const OperationalPeriodCreateSchema = z.object({
  start_at: z.string().datetime(),
  end_at: z.string().datetime(),
  status: z.enum(['planned', 'active']).optional().default('planned'),
  /** SITREP v3.3 — optional auto-issuance cadence in minutes (positive). Omit/null
   *  = the legacy midpoint+final behaviour. */
  sitrep_cadence_minutes: z.number().int().positive().nullable().optional(),
  declared_by_operator_id: z.string().max(128).optional(),
  session_id: z.string().min(1).max(128),
});
export type OperationalPeriodCreate = z.infer<typeof OperationalPeriodCreateSchema>;

/**
 * PATCH /api/operational-periods/:id — state transition and/or cadence change.
 * Both fields are optional but at least one must be present. `status` advances the
 * forward-only spine (planned→active→closed). `sitrep_cadence_minutes` sets the
 * SITREP v3.3 auto-issuance cadence; pass `null` to clear it (back to midpoint+final).
 */
export const OperationalPeriodPatchSchema = z
  .object({
    status: OperationalPeriodStatusSchema.optional(),
    sitrep_cadence_minutes: z.number().int().positive().nullable().optional(),
    session_id: z.string().min(1).max(128),
  })
  .refine((v) => v.status !== undefined || v.sitrep_cadence_minutes !== undefined, {
    message: 'provide status and/or sitrep_cadence_minutes',
  });
export type OperationalPeriodPatch = z.infer<typeof OperationalPeriodPatchSchema>;

/* ------------------------------------------------------------------ *
 * R5 Area Command spine (#493, migration 0123) — Ontario IMS §4.6 grouping requests.
 * The entity is an "Area Command" (oversight across multiple sites) — NEVER "Complex"
 * / "complex incident" / "unified command" on any operator surface (founder ruling
 * 2026-07-01). "Complex" (FIRE_COMPLEX_NAME) is backend-only, a later slice's signal.
 * ------------------------------------------------------------------ */

/**
 * POST /api/area-commands — ESTABLISH an Area Command: create the parent oversight
 * entity (is_area_command=true, hazard-agnostic). A first-class named Area Command
 * that may exist with zero children; child incidents are placed under it separately.
 * A centroid (lon/lat) is required — the same rule as a manual incident declaration.
 */
export const EstablishAreaCommandSchema = z.object({
  /** The Area Command name (reuses incidents.name). */
  name: z.string().min(1).max(256),
  /** Area Command centroid; [lon, lat]. Required (the parent is a geolocated incident row). */
  lon: z.number().min(-180).max(180),
  lat: z.number().min(-90).max(90),
  metadata: z.record(z.unknown()).optional(),
  session_id: z.string().min(1).max(128),
});
export type EstablishAreaCommand = z.infer<typeof EstablishAreaCommandSchema>;

/**
 * POST /api/area-commands/place — PLACE a child incident under an Area Command
 * (set child.parent_incident_id). Idempotent: re-placing under the same parent is a
 * no-op 200; a child already under a DIFFERENT Area Command is a 409 (remove first).
 */
export const PlaceUnderAreaCommandSchema = z.object({
  /** The Area Command parent (must be is_area_command=true). */
  parent_incident_id: z.string().uuid(),
  /** The child incident to group (must NOT itself be an Area Command; must not equal the parent). */
  child_incident_id: z.string().uuid(),
  session_id: z.string().min(1).max(128),
});
export type PlaceUnderAreaCommand = z.infer<typeof PlaceUnderAreaCommandSchema>;

/**
 * POST /api/area-commands/remove — REMOVE a child incident from its Area Command
 * (null child.parent_incident_id). Always reversible; the child survives, un-grouped.
 */
export const RemoveFromAreaCommandSchema = z.object({
  child_incident_id: z.string().uuid(),
  session_id: z.string().min(1).max(128),
});
export type RemoveFromAreaCommand = z.infer<typeof RemoveFromAreaCommandSchema>;

/**
 * R5 Area Command Slice 5 (#541, migration 0129) — PUT /api/area-commands/:id/objectives —
 * REPLACE the Area Command PARENT's ordered list of BROAD OBJECTIVES (Ontario IMS §4.6 /
 * App A #2: "develop broad objectives for their assigned area"). Replace-whole-ordered-list
 * semantics — the array's ORDER is the objectives' order (ordinal = index + 1). Set on the
 * is_area_command parent ONLY (the endpoint 422s on a non-Area-Command incident); NEVER a
 * child incident's IAP. An empty array clears all objectives.
 */
export const SetAreaCommandObjectivesSchema = z.object({
  /** The ordered broad objectives (each a non-empty statement); [] clears the list. */
  objectives: z.array(z.string().trim().min(1).max(2000)).max(50),
  session_id: z.string().min(1).max(128),
});
export type SetAreaCommandObjectives = z.infer<typeof SetAreaCommandObjectivesSchema>;

/**
 * R5 Area Command Slice 5 (#541, migration 0129) — PUT /api/area-commands/:id/resource-priorities
 * — REPLACE the Area Command PARENT's ordered list of PRIORITIES FOR THE SHARING OF CRITICAL
 * RESOURCES across incident sites (App A #2). A priority STATEMENT — never an allocation or
 * tasking of a child (§4.6: the AC "works with" the IMTs). Same replace-whole-ordered-list
 * semantics + is_area_command gate as SetAreaCommandObjectivesSchema.
 */
export const SetAreaCommandResourcePrioritiesSchema = z.object({
  /** The ordered resource-sharing priority statements; [] clears the list. */
  resource_priorities: z.array(z.string().trim().min(1).max(2000)).max(50),
  session_id: z.string().min(1).max(128),
});
export type SetAreaCommandResourcePriorities = z.infer<typeof SetAreaCommandResourcePrioritiesSchema>;

/**
 * R5 Area Command Slice 2 (#494) — a lightweight reference to an Area Command (§4.6)
 * parent: enough to render the "Under Area Command — <name>" membership badge and open
 * the parent, without re-fetching. NOT a full incident.
 */
export const AreaCommandRefSchema = z.object({
  id: z.string(),
  incident_number: z.string(),
  name: z.string(),
});
export type AreaCommandRef = z.infer<typeof AreaCommandRefSchema>;

/* ------------------------------------------------------------------ *
 * Responses
 * ------------------------------------------------------------------ */

export const IncidentSchema = z.object({
  id: z.string(),
  incident_number: z.string(),
  name: z.string(),
  hazard_type: IncidentHazardTypeSchema,
  status: IncidentStatusSchema,
  /** R1/#477 — the wildfire lifecycle phase driving marker symbology (migration 0117). */
  lifecycle_state: IncidentLifecycleStateSchema,
  opened_at: z.string(),
  closed_at: z.string().nullable(),
  source_record_type: z.string().nullable(),
  source_record_id: z.string().nullable(),
  lon: z.number(),
  lat: z.number(),
  metadata: z.record(z.unknown()),
  /** Count of communities linked to this incident via an incident-tied headcount
   *  or wellness check (§4.30). Drives the data-driven Evacuation Logistics gate:
   *  the entry button shows when this is ≥1, regardless of hazard. Present on the
   *  read endpoints (GET list + GET :id); omitted on the promote/create response. */
  linked_community_count: z.number().int().nonnegative().optional(),
  /** R5 Area Command spine (#493, migration 0123) — the Area Command (§4.6) this
   *  incident is grouped UNDER, or null. A child incident points up to its parent;
   *  null for a normal incident and for the Area Command parent itself. */
  parent_incident_id: z.string().nullable(),
  /** R5 Area Command spine (#493, migration 0123) — TRUE when this row IS an Area
   *  Command oversight entity (§4.6 — oversees multiple sites), not a hazard incident.
   *  The parent reuses `name` (Area Command name) + the incident_command roster (the
   *  Area Command lead; unassigned = no live lead). "Complex" is NEVER a UI label. */
  is_area_command: z.boolean(),
  /** #445-ws2 (migration 0148) — the OPERATOR-ASSESSED Response Level the IC set on this
   *  incident (Ontario IMS §5 / §6.1), or null when unset. The RAW operator pin — the
   *  reconciled effective/derived/escalation-safe block lives on IncidentDetail
   *  (`response_level`), which is where the system-derived max-fold is computed. Present on
   *  the read endpoints; omitted on the promote/create response. */
  operator_set_response_level: z.number().int().min(1).max(4).nullable().optional(),
  /** #445-ws2 — the required assessment reason for the operator pin (null when unset). */
  operator_rl_reason: z.string().nullable().optional(),
  /** #445-ws2 — when the latest operator assessment landed (ISO), or null. */
  operator_rl_at: z.string().nullable().optional(),
  /** #445-ws2 — the assessing Incident Commander (command_officer_name snapshot), or null. */
  operator_rl_by: z.string().nullable().optional(),
});
export type Incident = z.infer<typeof IncidentSchema>;

/**
 * #445-ws2 (migration 0148) — the reconciled Response Level block on an incident detail.
 * Ontario IMS §5 Table 1 (Small/Large/Major/Emergency). The COP carries BOTH the
 * SYSTEM-DERIVED level (the per-hazard ladder max-fold — never mutated) AND the
 * OPERATOR-ASSESSED level the IC sets (records-not-confers). The #398 single-column
 * overwrite is deliberately NOT copied — both are always shown.
 */
export const IncidentResponseLevelSchema = z.object({
  /** The OPERATIVE level (`operator_set ?? derived`) — what the IC asserts. Drives the
   *  incident badge; shown NEXT TO the annotated system-derived. Null when neither exists. */
  effective: z.number().int().min(1).max(4).nullable(),
  /** The SYSTEM-DERIVED level: the max-fold of this incident's contributing-hazard
   *  severities (the doctrine "header max-fold"). Null when no scored hazard. Never mutated. */
  derived: z.number().int().min(1).max(4).nullable(),
  /** The IC's operator-assessed level, or null when unset. */
  operator_set: z.number().int().min(1).max(4).nullable(),
  /** The escalation-safe level = the HIGHER of the operative and the system-derived. The
   *  urgent-queue gate / disc / SITREP fold key off this so an IC de-escalation can never
   *  bury a worse live derived (the de-escalation guard). */
  escalation_safe: z.number().int().min(1).max(4).nullable(),
  /** True when the system-derived EXCEEDS the operator pin — the worsening-hazard divergence
   *  a de-escalation must surface prominently. */
  derived_exceeds_operator: z.boolean(),
  /** The required assessment reason for the operator pin, or null when unset. */
  reason: z.string().nullable(),
  /** When the latest operator assessment landed (ISO), or null when unset. */
  assessed_at: z.string().nullable(),
  /** The assessing Incident Commander (command_officer_name snapshot), or null. */
  assessed_by: z.string().nullable(),
});
export type IncidentResponseLevel = z.infer<typeof IncidentResponseLevelSchema>;

/** POST /api/incidents/:id/response-level — the IC SETs / Escalates / De-escalates the
 *  operator-assessed level. The reason is REQUIRED (records-not-confers assessment note). */
export const SetIncidentResponseLevelSchema = z.object({
  response_level: z.number().int().min(1).max(4),
  reason: z.string().min(1).max(500),
  session_id: z.string().min(1).max(128).optional(),
});
export type SetIncidentResponseLevel = z.infer<typeof SetIncidentResponseLevelSchema>;

/** DELETE /api/incidents/:id/response-level — the IC CLEARS the operator pin (the incident
 *  reverts to the system-derived). An optional reason is recorded on the clear ledger row. */
export const ClearIncidentResponseLevelSchema = z.object({
  reason: z.string().max(500).nullable().optional(),
  session_id: z.string().min(1).max(128).optional(),
});
export type ClearIncidentResponseLevel = z.infer<typeof ClearIncidentResponseLevelSchema>;

export const OperationalPeriodSchema = z.object({
  id: z.string(),
  incident_id: z.string(),
  period_number: z.number().int(),
  start_at: z.string(),
  end_at: z.string(),
  status: OperationalPeriodStatusSchema,
  /** SITREP v3.3 — auto-issuance cadence in minutes; null = midpoint+final default. */
  sitrep_cadence_minutes: z.number().int().positive().nullable(),
  declared_by_operator_id: z.string().nullable(),
  created_at: z.string(),
});
export type OperationalPeriod = z.infer<typeof OperationalPeriodSchema>;

/** A SITREP reference in an incident-detail response (lighter than the full row). */
export const IncidentSitrepRefSchema = z.object({
  id: z.string(),
  created_at: z.string(),
  title: z.string(),
  report_version: z.enum(['standalone', 'initial', 'update', 'final']),
  report_sequence: z.number().int().nullable(),
  operational_period_id: z.string().nullable(),
  status: z.string(),
  /** Derived at read-time from the operator_actions audit trail (auto_generate_sitrep
   *  vs generate_sitrep) — true when the scheduler issued it, false when an operator
   *  did. Not persisted on sitrep_reports; defaults false when no audit row exists. */
  auto_generated: z.boolean().default(false),
});
export type IncidentSitrepRef = z.infer<typeof IncidentSitrepRefSchema>;

/* ------------------------------------------------------------------ *
 * #427c — the incident's HAZARD COMPOSITION (aggregated-hazard drill-in).
 * ------------------------------------------------------------------ */

/**
 * The link models a contributing hazard reaches this incident by:
 *   • `promotion`  — the incident's promotion PROVENANCE (source_record_type/id): the
 *      single hazard the incident was promoted from (hotspot / fire / CAP / flood / ice).
 *   • `aggregator` — a hazard row that points AT this incident via `.incident_id`
 *      (cap_alerts / arcgis_public_features). One incident has ONE promotion source but
 *      may aggregate many.
 */
export const ContributingHazardViaSchema = z.enum(['promotion', 'aggregator']);
export type ContributingHazardVia = z.infer<typeof ContributingHazardViaSchema>;

/** The hazard classes a contributing hazard can come from. Evacuation classes
 *  (operator_evacuation_orders, arcgis category='evacuation') are DELIBERATELY absent —
 *  an evacuation is a PROTECTIVE ACTION (Ontario IMS), surfaced in `protective_actions`,
 *  NOT a hazard front (founder ruling 2026-07-04, hazard-fronts-only). */
export const ContributingHazardSourceKindSchema = z.enum([
  'high_risk_hotspot',
  'active_wildland_fire',
  'flood_anomaly',
  'ice_storm_anomaly',
  'cap_alert',
  'arcgis_feature',
]);
export type ContributingHazardSourceKind = z.infer<typeof ContributingHazardSourceKindSchema>;

/** How a contributing hazard's SEVERITY was derived (or why it is null):
 *   • `response_level`  — the class carries a numeric response_level (hotspot / cap /
 *      flood / ice); 1..4.
 *   • `oc_fire_ladder`  — a wildfire has no response_level column; severity is derived by
 *      the OC-fire ladder (oc-fire-rl.ts). NULL for a fire that is not Out of Control
 *      (an honest gap — a monitored/held fire carries no urgent severity).
 *   • `native_severity` — the class carries NO numeric severity (arcgis features); only a
 *      native severity TEXT (in `severity_label`). severity is null (the honest gap — a
 *      fabricated Response Level is forbidden). */
export const ContributingHazardSeveritySourceSchema = z.enum([
  'response_level',
  'oc_fire_ladder',
  'native_severity',
]);
export type ContributingHazardSeveritySource = z.infer<
  typeof ContributingHazardSeveritySourceSchema
>;

/**
 * ONE hazard front contributing to an incident's HAZARD COMPOSITION (#427c). This is NOT
 * a per-hazard Response Level: Ontario IMS §5 Table 1 classifies the INCIDENT's response
 * STRUCTURE — one incident has ONE Response Level (the header max-fold). This carries the
 * hazard's TYPE + a contributing SEVERITY (1..4, sharing the Response Level palette for
 * the DOT colour only), never a per-hazard "Response Level"/"RL".
 */
export const ContributingHazardSchema = z.object({
  source_kind: ContributingHazardSourceKindSchema,
  /** The source hazard row id (uuid text). Unique per (source_kind, source_id). */
  source_id: z.string(),
  /** Hazard-type token for the type chip; an IncidentHazardType where one fits
   *  (wildfire / flood / ice_storm / cap_warning) else the raw class category
   *  (arcgis flood / drought / tsunami). `hazardStyle` resolves label + icon. */
  hazard_type: z.string(),
  /** Contributing severity 1..4, or null where the class carries no numeric severity
   *  (arcgis features; a non-OC fire). NEVER fabricated. */
  severity: z.number().int().min(1).max(4).nullable(),
  severity_source: ContributingHazardSeveritySourceSchema,
  /** Native severity TEXT for the null-severity honest gap (the arcgis `severity`
   *  string, e.g. "major"), else null. */
  severity_label: z.string().nullable(),
  via: ContributingHazardViaSchema,
});
export type ContributingHazard = z.infer<typeof ContributingHazardSchema>;

/**
 * #485 — ONE completed deactivation→reactivation cycle, derived at read-time from the
 * advance_incident_lifecycle audit ledger. The incident was Deactivated (§8) at
 * `deactivated_at` (a `close_incident` OR an advance to 'deactivated') and later Reactivated
 * at `reactivated_at` (the deactivated→confirmed back-edge, W7/#535 — via the advance route OR
 * a protective-action re-link). Both bounds are ISO-8601 UTC. Nothing here is persisted — it
 * is reconstructed from the ledger so the honest gap survives even though closed_at is cleared
 * on reopen.
 */
export const IncidentReactivationSchema = z.object({
  deactivated_at: z.string(),
  reactivated_at: z.string(),
});
export type IncidentReactivation = z.infer<typeof IncidentReactivationSchema>;

/** GET /api/incidents/:id — detail with related periods + SITREPs, plus the R2/#478
 *  protective actions (linked/auto-linked evac + life-safety CAP) collapsed into this
 *  incident and the communities their zones cover (the coverage-read). */
export const IncidentDetailSchema = IncidentSchema.extend({
  operational_periods: z.array(OperationalPeriodSchema),
  sitreps: z.array(IncidentSitrepRefSchema),
  /** R2/#478 — evac orders / life-safety CAPs that are protective-action attributes of
   *  this incident (persisted MANUAL links + the read-time AUTO-link projection). */
  protective_actions: z.array(ProtectiveActionSchema),
  /** R2/#478 — communities whose footprint the protective-action zone(s) cover
   *  (ST_Intersects service_area OR ST_Contains centroid) — the Municipal EOC's
   *  "my community is in-zone" read. */
  in_zone_communities: z.array(InZoneCommunitySchema),
  /** W3 spine (#489, migration 0120) — the structured INCIDENT COMMAND block: the lead
   *  organization + command officer (single command). NULL until the incident is owned
   *  (lifecycle_state='owned'/'response'); `owned` ⟺ a live lead participation. */
  command: IncidentCommandSchema.nullable(),
  /** R5 Area Command spine (#493, migration 0123) — when this incident IS an Area Command
   *  (§4.6, is_area_command=true), the child incidents grouped under it (each keeping its
   *  OWN command/lifecycle/SITREP — resolve a child's full detail via its own GET :id).
   *  Empty for a normal incident and for a childless Area Command. */
  children: z.array(IncidentSchema),
  /** R5 Area Command Slice 2 (#494) — a lightweight summary of the Area Command (§4.6)
   *  this incident is grouped UNDER (resolved from parent_incident_id), or null. Powers
   *  the child's "Under Area Command — <name>" membership badge without a second fetch. */
  parent: AreaCommandRefSchema.nullable(),
  /** R5 Area Command Slice 5 (#541, migration 0129) — the Area Command PARENT's OWN
   *  ordered BROAD OBJECTIVES (App A #2: "develop broad objectives for their assigned
   *  area"). Populated ONLY when this incident IS an Area Command (is_area_command=true);
   *  an EMPTY array for a normal incident and for a childless/unset Area Command. */
  area_command_objectives: z.array(z.string()),
  /** R5 Area Command Slice 5 (#541, migration 0129) — the Area Command PARENT's OWN
   *  ordered PRIORITIES for the sharing of critical resources across sites (App A #2).
   *  Same is_area_command-only population; empty otherwise. A priority STATEMENT — never
   *  a child allocation/tasking (§4.6). */
  area_command_resource_priorities: z.array(z.string()),
  /** #427c — the incident's HAZARD COMPOSITION: which hazard fronts contribute, at what
   *  contributing severity (type · severity, NOT per-hazard Response Level). Enumerates
   *  BOTH link models (promotion provenance + `.incident_id` aggregators), de-duped by
   *  (source_kind, source_id). Evacuations are protective actions, not hazards, and are
   *  excluded (see `protective_actions`). Empty for a manually-declared incident with no
   *  linked hazards. */
  contributing_hazards: z.array(ContributingHazardSchema),
  /** #445-ws2 (migration 0148) — the reconciled Response Level: the OPERATIVE (effective)
   *  level the IC asserts, the SYSTEM-DERIVED max-fold (always shown, never mutated), the
   *  escalation-safe max the urgent surfaces use, the divergence flag, and the assessment
   *  attribution (assessed_by / reason / assessed_at). Always present — a manual incident
   *  with no operator pin and no scored hazard reads all-null with derived_exceeds_operator
   *  false. */
  response_level: IncidentResponseLevelSchema,
  /** #485 — read-time REACTIVATION history, DERIVED from the advance_incident_lifecycle audit
   *  ledger (NOT persisted; closed_at is deliberately NOT re-populated on reopen — that would
   *  lie the other way). Each entry is a completed cycle: the incident was Deactivated (§8) at
   *  `deactivated_at` and later Reactivated (the deactivated→confirmed back-edge, W7/#535) at
   *  `reactivated_at`, oldest first. EMPTY for an incident never reactivated. After a reopen
   *  closed_at is NULL (the file is open again), so this is the only honest record of the gap:
   *  the header renders the LATEST entry as "Reactivated <T2> · was deactivated <T1>" instead of
   *  reading continuously-active from opened_at. */
  lifecycle_reactivations: z.array(IncidentReactivationSchema),
});
export type IncidentDetail = z.infer<typeof IncidentDetailSchema>;

export const IncidentListResponseSchema = z.object({
  incidents: z.array(IncidentSchema),
});
export type IncidentListResponse = z.infer<typeof IncidentListResponseSchema>;

/**
 * R5 Area Command Slice 2 (#494) — GET /api/area-commands/proposals — a read-time
 * MEMBERSHIP PROPOSAL over ungrouped open wildfire incidents. NEVER auto-merges; the
 * operator confirms. `kind` is the machine-readable rationale (the operator-facing
 * rationale copy is rendered web-side from the doctrine-locked incidentVocabulary):
 *   - 'shared_grouping' — the members' source fires share a management grouping
 *     (backend signal FIRE_COMPLEX_NAME — NEVER surfaced; §4.6 doctrine).
 *   - 'proximity'       — the members are within AREA_COMMAND_PROXIMITY_KM of each other.
 * `existing_area_command` set ⇒ the proposal ADDS the members to that adjacent Area
 * Command (place-only); null ⇒ ESTABLISH a new Area Command over the members.
 */
export const AreaCommandProposalKindSchema = z.enum(['shared_grouping', 'proximity']);
export type AreaCommandProposalKind = z.infer<typeof AreaCommandProposalKindSchema>;

export const AreaCommandProposalSchema = z.object({
  /** Stable client key = deterministic over the sorted member ids (+ existing AC id).
   *  Lets the UI track dismissals across refetches without a persisted proposal entity. */
  proposal_id: z.string(),
  kind: AreaCommandProposalKindSchema,
  /** Candidate child incidents to group (all ungrouped, open, hazard_type='wildfire'). */
  members: z.array(IncidentSchema),
  /** Cluster centroid (mean of member points) — the establish lon/lat. */
  centroid: z.object({ lon: z.number(), lat: z.number() }),
  /** Neutral, operator-editable suggested Area Command name (nearest populated place).
   *  Data, not doctrine copy — never the FIRE_COMPLEX_NAME value. */
  suggested_name: z.string(),
  /** proximity only — the max pairwise separation within the cluster, km (rounded);
   *  0 for a shared_grouping proposal. Drives the "~N km apart" rationale copy. */
  spread_km: z.number().int().nonnegative(),
  /** When set, the proposal ADDS the members to this EXISTING Area Command (no establish). */
  existing_area_command: AreaCommandRefSchema.nullable(),
});
export type AreaCommandProposal = z.infer<typeof AreaCommandProposalSchema>;

export const AreaCommandProposalsResponseSchema = z.object({
  proposals: z.array(AreaCommandProposalSchema),
});
export type AreaCommandProposalsResponse = z.infer<typeof AreaCommandProposalsResponseSchema>;

export const OperationalPeriodListResponseSchema = z.object({
  operational_periods: z.array(OperationalPeriodSchema),
});
export type OperationalPeriodListResponse = z.infer<typeof OperationalPeriodListResponseSchema>;
